import { Link, Navigate, Route, Routes } from 'react-router-dom';
import type { ClavePermiso } from '@copathy/shared';
import { usePermisos as usePermisosDeSesion } from '../lib/auth';
import { useMigas } from '../lib/migas';
import { Contenedor } from '../componentes/Contenedor';
import { BotonVolver } from '../componentes/BotonVolver';
import { PanelCatalogos } from './ConfigCatalogos';
import { PanelRoles } from './ConfigRoles';
import { ConfigRolDetalle } from './ConfigRolDetalle';
import { PanelOrganizacion } from './ConfigOrganizacion';
import { Ajustes, Edificio, Escudo, Lista } from '../componentes/iconos';

/**
 * Configuración de la plataforma.
 *
 * ⚠️ **Usuarios NO está acá.** Tiene su propia entrada en el menú porque se usa a
 * diario, y repetirlo como tarjeta obligaba a mantener dos caminos a la misma
 * pantalla. Configuración es lo que se ajusta una vez y queda funcionando.
 *
 * ── Por qué tarjetas con ruta propia y no pestañas ──
 * Criterio portado de Polaris. Cada sección de acá es un mundo aparte y varias
 * tienen su propio detalle dentro. Con pestañas todo cuelga de la misma URL: no
 * se puede enlazar a una sección, el botón de volver del navegador saca de
 * Configuración entera, y las secciones compiten por la atención sin decir qué
 * hace cada una. Con tarjetas hay un índice que explica, y cada sección vive en
 * su ruta.
 */
type Seccion = {
  clave: string;
  titulo: string;
  descripcion: string;
  icono: typeof Ajustes;
  permiso: ClavePermiso;
};

const SECCIONES: Seccion[] = [
  {
    clave: 'organizacion',
    titulo: 'Datos de la empresa',
    descripcion: 'La ficha de Copathy: nombre, contacto y el relato de marca.',
    icono: Edificio,
    // `empresas.ver` para entrar; editar pide `empresas.editar`, y la pantalla
    // lo comprueba. Quien solo puede mirar entra y ve los campos apagados.
    permiso: 'empresas.ver',
  },
  {
    clave: 'roles',
    titulo: 'Roles y permisos',
    descripcion: 'Qué puede hacer cada rol. Los cuatro son fijos; lo que se ajusta es su alcance.',
    icono: Escudo,
    permiso: 'roles.administrar',
  },
  {
    clave: 'catalogos',
    titulo: 'Catálogos',
    descripcion: 'Las listas que alimentan los campos: sectores, áreas, cargos, competencias y programas.',
    icono: Lista,
    permiso: 'catalogos.administrar',
  },
];

function useSecciones(): Seccion[] {
  const permisos = usePermisosDeSesion();
  return SECCIONES.filter((s) => permisos.includes(s.permiso));
}

function Indice() {
  const secciones = useSecciones();
  useMigas([{ texto: 'Configuración' }]);

  // Sin ninguna sección visible, esta pantalla no tiene nada que ofrecer.
  if (secciones.length === 0) return <Navigate to="/" replace />;

  return (
    <Contenedor>
      <h1 className="text-2xl font-bold text-marino-800">Configuración</h1>
      <p className="mt-1 text-sm text-marino-400">
        Lo que se ajusta una vez y queda funcionando.
      </p>

      <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {secciones.map((s) => (
          <li key={s.clave}>
            <Link
              to={`/configuracion/${s.clave}`}
              className="group flex h-full items-start gap-4 rounded-2xl border border-marino-100
                bg-white p-5 transition hover:border-marino-300 hover:shadow-sm
                focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
            >
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-marino-50 text-marino-600">
                <s.icono size={22} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-semibold text-marino-800">{s.titulo}</span>
                <span className="mt-1 block text-sm text-marino-400">{s.descripcion}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </Contenedor>
  );
}

/** Envoltura de una sección: título, camino de vuelta y el contenido. */
function Seccion({ clave, children }: { clave: string; children: React.ReactNode }) {
  const secciones = useSecciones();
  const s = secciones.find((x) => x.clave === clave);
  useMigas([{ texto: 'Configuración', a: '/configuracion' }, { texto: s?.titulo ?? 'Sección' }]);

  // Sin permiso para esta sección, de vuelta al índice, que decide qué mostrar.
  if (!s) return <Navigate to="/configuracion" replace />;

  return (
    <Contenedor>
      {/*
        Se llega acá desde el índice, que no está en el menú lateral: sin este
        botón el único camino de vuelta es el del navegador.
      */}
      <BotonVolver a="/configuracion">Configuración</BotonVolver>
      <div className="mb-5 mt-4">
        <h1 className="text-2xl font-bold text-marino-800">{s.titulo}</h1>
        <p className="mt-1 text-sm text-marino-400">{s.descripcion}</p>
      </div>
      {children}
    </Contenedor>
  );
}

export function Configuracion() {
  return (
    <Routes>
      <Route index element={<Indice />} />
      <Route
        path="organizacion"
        element={
          <Seccion clave="organizacion">
            <PanelOrganizacion />
          </Seccion>
        }
      />
      <Route
        path="roles"
        element={
          <Seccion clave="roles">
            <PanelRoles />
          </Seccion>
        }
      />
      {/*
        La ficha de un rol trae su propio título y su vuelta al listado: va SIN la
        envoltura de `Seccion`, que le pondría una segunda cabecera y un segundo
        botón de volver — a Configuración, no al listado, que es lo que uno espera.
      */}
      <Route path="roles/:id" element={<ConfigRolDetalle />} />
      <Route
        path="catalogos"
        element={
          <Seccion clave="catalogos">
            <PanelCatalogos />
          </Seccion>
        }
      />
      {/* Cualquier otra cosa dentro de /configuracion vuelve al índice. */}
      <Route path="*" element={<Navigate to="/configuracion" replace />} />
    </Routes>
  );
}
